import React from 'react';
import { bool, arrayOf, number, shape } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { ensureCurrentUser, ensureUser } from '../../util/data';
import { withViewport } from '../../util/uiHelpers';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { Page, LayoutSideNavigation, UserNav, } from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';
import PartnerProfileHeaderBanner from '../PartnerProfileHeaderBanner/PartnerProfileHeaderBanner';
import AsideContent from './components/AsideContent/AsideContent';
import MainContent from './components/MainContent/MainContent';

import {
  BUSINESS,
  EQUIPMENT_PROCUREMENT,
  INDIVIDUAL,
  NO_SUBSCRIPTION_PLAN,
  EQUIPMENT_PROVIDER,
  SUBSCRIPTION_PLAN_PREMIUM,
  SUBSCRIPTION_PLAN_BUSINESS,
  SUBSCRIPTION_PLAN_PREMIER,
} from '../../constants';

import css from './ProfilePage.module.css';

const ProfilePageComponent = props => {
  const config = useConfiguration();
  const {
    scrollingDisabled,
    currentUser,
    userShowError,
    user,
    intl,
    subscriptionPlan,
    listings,
    pagination,
    queryParams,
    userId,
    onManageDisableScrolling,
    ...rest
  } = props;

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const profileUser = ensureUser(user);
  const isCurrentUser =
    ensuredCurrentUser.id && profileUser.id && ensuredCurrentUser.id.uuid === profileUser.id.uuid;
  const { bio, displayName, publicData } = profileUser?.attributes?.profile || {};
  const { accountBusinessType, accountType, brandDetails, companyLegalName } = publicData ?? {};
  const isBuyer =
    accountType === INDIVIDUAL ||
    (accountType === BUSINESS && accountBusinessType === EQUIPMENT_PROCUREMENT);

  const isBuyerWithSubscription =
    accountType === BUSINESS &&
    accountBusinessType === EQUIPMENT_PROVIDER &&
    subscriptionPlan &&
    [SUBSCRIPTION_PLAN_BUSINESS, SUBSCRIPTION_PLAN_PREMIER, SUBSCRIPTION_PLAN_PREMIUM]
      .find(p => p.toLowerCase() === subscriptionPlan.toLowerCase());

  const {
    headerBanner = [],
    url: websiteUrl,
    socials = [],
    companyDescription = "",
  } = brandDetails ?? {};

  const isStandardPartners =
    accountType === BUSINESS &&
    accountBusinessType === EQUIPMENT_PROVIDER &&
    subscriptionPlan &&
    subscriptionPlan.toLowerCase() === NO_SUBSCRIPTION_PLAN.toLowerCase();

  const showReviewAsCustomer =
    accountType === BUSINESS &&
    accountBusinessType === EQUIPMENT_PROCUREMENT;

  const isPremiumPartners =
    accountType === BUSINESS &&
    accountBusinessType === EQUIPMENT_PROVIDER &&
    subscriptionPlan &&
    subscriptionPlan.toLowerCase() === SUBSCRIPTION_PLAN_PREMIUM.toLowerCase();

  const schemaTitleVars = { name: companyLegalName ?? displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  if (userShowError && userShowError.status === 404) {
    return <NotFoundPage />;
  }
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        name: schemaTitle,
      }}
    >
      <LayoutSideNavigation
        sideNavClassName={css.aside}
        topbar={
          <>
            <TopbarContainer currentPage="ProfilePage" />
            {isCurrentUser ? <UserNav
              currentPage="ProfilePage"
              currentUser={currentUser}
            /> : null}

            {isBuyer || isStandardPartners ? null : (
              <PartnerProfileHeaderBanner
                companyDescription={companyDescription}
                companyLegalName={companyLegalName}
                websiteUrl={websiteUrl}
                bannerUrl={headerBanner[0]}
                socials={socials}
              />
            )}
          </>
        }
        sideNav={
          <AsideContent
            user={user}
            isCurrentUser={isCurrentUser}
            displayName={displayName}
            isBuyerWithSubscription={isBuyerWithSubscription}
          />
        }
        usePartnerProfileSideNav={!!isBuyerWithSubscription}
        currentPage="ProfilePage"
        footer={<FooterContainer />}
        userId={userId?.uuid}
        mainColumnClassName={css.mainColumnClassName}
      >
        <MainContent
          isBuyer={isBuyer}
          bio={bio}
          displayName={displayName}
          userShowError={userShowError}
          showReviewAsCustomer={showReviewAsCustomer}
          isPremiumPartners={isPremiumPartners}
          currentPage="ProfilePage"
          pagination={!!listings ? pagination : null}
          queryParams={queryParams}
          listings={listings}
          onManageDisableScrolling={onManageDisableScrolling}
          intl={intl}
          config={config}
          userId={userId?.uuid}
          {...rest}
        />
      </LayoutSideNavigation>
    </Page>
  );
};

ProfilePageComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
};

ProfilePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  user: propTypes.user,
  userShowError: propTypes.error,
  queryListingsError: propTypes.error,
  listings: arrayOf(propTypes.listing).isRequired,
  reviews: arrayOf(propTypes.review),
  queryReviewsError: propTypes.error,

  // form withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews,
    queryReviewsError,
    subscriptionPlan,
    pagination,
    queryParams,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;
  const listings = getMarketplaceEntities(state, userListingRefs);
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    user,
    userShowError,
    queryListingsError,
    listings,
    reviews,
    queryReviewsError,
    subscriptionPlan,
    pagination,
    queryParams,
    userId,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const ProfilePage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withViewport,
  injectIntl
)(ProfilePageComponent);

export default ProfilePage;
